import React, { useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate, useLocation } from "react-router-dom";

const navItems = ["ABOUT", "OUTLETS", "CATERING", "CONTACT"];
const outlets = [
  { name: "City Light - Surat", id: "kulche-shulche-citylight" },
  { name: "Piplod - Surat", id: "kulche-shulche-piplod" },
  { name: "Pal Adajan - Surat", id: "kulche-shulche-pal" },
  { name: "Katargam - Surat", id: "kulche-shulche-katargam" },
];

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedNav, setSelectedNav] = React.useState("HOME");
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorElDesktop, setAnchorElDesktop] = React.useState(null);
  const [anchorElMobile, setAnchorElMobile] = React.useState(null);

  useEffect(() => {
    if (location.pathname.startsWith("/outlets")) {
      setSelectedNav("OUTLETS");
    } else {
      switch (location.pathname) {
        case "/": setSelectedNav("HOME"); break;
        case "/contact": setSelectedNav("CONTACT"); break;
        case "/about": setSelectedNav("ABOUT"); break;
        case "/store-locator": setSelectedNav("STORE LOCATOR"); break;
        case "/catering": setSelectedNav("CATERING"); break;
        default: setSelectedNav(""); break;
      }
    }
  }, [location.pathname]);

  const handleNavClick = (nav) => {
    setSelectedNav(nav);
    setMobileOpen(false);
    switch (nav) {
      case "HOME": navigate("/"); break;
      case "CONTACT": navigate("/contact"); break;
      case "ABOUT": navigate("/about"); break;
      case "CATERING": navigate("/catering"); break;
      case "STORE LOCATOR": navigate("/store-locator"); break;
      default: break;
    }
  };

  const handleOutletDesktopOpen = (e) => setAnchorElDesktop(e.currentTarget);
  const handleOutletDesktopClose = () => setAnchorElDesktop(null);

  const handleOutletMobileOpen = (e) => setAnchorElMobile(e.currentTarget);
  const handleOutletMobileClose = () => setAnchorElMobile(null);

  const handleOutletClick = (out) => {
    setSelectedNav("OUTLETS");
    handleOutletDesktopClose();
    handleOutletMobileClose();
    setMobileOpen(false);
    navigate(`/outlets/${out.id}`);
  };

  const toggleDrawer = () => setMobileOpen(!mobileOpen);

  const mobileDrawer = (
    <Box sx={{ width: 250, px: 2, py: 3 }} role="presentation">
      <Box
        component="img"
        src="/KS.png"
        alt="Logo"
        sx={{ width: "100%", mb: 2, cursor: "pointer" }}
        onClick={() => handleNavClick("HOME")}
      />
      <List>
        <ListItem button onClick={() => handleNavClick("HOME")}>
          <ListItemText
            primary="HOME"
            primaryTypographyProps={{
              fontWeight: selectedNav === "HOME" ? "bold" : "normal",
              color: selectedNav === "HOME" ? "#FCA500" : "inherit",
            }}
          />
        </ListItem>
        <ListItem>
          <Button
            onClick={handleOutletMobileOpen}
            endIcon={<ExpandMoreIcon />}
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              textTransform: 'uppercase',
              color: selectedNav === "OUTLETS" ? "#FCA500" : "#000",
              fontWeight: selectedNav === "OUTLETS" ? 'bold' : 'normal',
              px: 0,
            }}
          >
            OUTLETS
          </Button>
        </ListItem>
        <Menu
          anchorEl={anchorElMobile}
          open={Boolean(anchorElMobile)}
          onClose={handleOutletMobileClose}
          PaperProps={{ elevation: 3, sx: { borderRadius: 2, minWidth: 180, mt: 1 } }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          {outlets.map((out) => (
            <MenuItem key={out.id} onClick={() => handleOutletClick(out)} sx={{ px: 2.5, py: 1.25 }}>
              {out.name}
            </MenuItem>
          ))}
        </Menu>
        {["ABOUT", "CATERING", "CONTACT"].map((item) => (
          <ListItem button key={item} onClick={() => handleNavClick(item)}>
            <ListItemText
              primary={item}
              primaryTypographyProps={{
                fontWeight: selectedNav === item ? "bold" : "normal",
                color: selectedNav === item ? "#FCA500" : "inherit",
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#FFF8E1", px: { xs: 0, sm: 2 }, pt: { xs: 0.5, sm: 2 } }}>
      <Container maxWidth={false}>
        <Box sx={{ borderRadius: "0 0 32px 32px", overflow: "hidden", boxShadow: 3 }}>
          <AppBar position="static" elevation={0} sx={{ bgcolor: "#ffffff", width: "100%", px: { xs: 2, md: 4 }, py: { xs: 1, md: 1.5 } }}>
            <Toolbar sx={{ justifyContent: "space-between" }}>
              <Box component="img" src="KS.png" alt="Kulche Shulche Logo" sx={{ height: 70, cursor: "pointer" }} onClick={() => handleNavClick("HOME")} />

              {isMobile ? (
                <>
                  <IconButton onClick={toggleDrawer} color="inherit">
                    <MenuIcon sx={{ color: "#FCA500" }} />
                  </IconButton>
                  <Drawer anchor="right" open={mobileOpen} onClose={toggleDrawer}>
                    {mobileDrawer}
                  </Drawer>
                </>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <Button
                    variant={selectedNav === "HOME" ? "contained" : "text"}
                    onClick={() => handleNavClick("HOME")}
                    sx={{
                      bgcolor: selectedNav === "HOME" ? "#FCA500" : "transparent",
                      color: selectedNav === "HOME" ? "#fff" : "#FCA500",
                      px: 3, py: 1, borderRadius: "20px", fontSize: "0.875rem",
                      textTransform: "uppercase", fontWeight: "bold",
                      "&:hover": { bgcolor: "#ea650c", color: "#fff" },
                    }}
                  >
                    Home
                  </Button>

                  {navItems.map((item) =>
                    item === "OUTLETS" ? (
                      <Box key={item}>
                        <Button
                          onClick={handleOutletDesktopOpen}
                          endIcon={<ExpandMoreIcon />}
                          sx={{
                            color: selectedNav === "OUTLETS" ? "#fff" : "#FCA500",
                            bgcolor: selectedNav === "OUTLETS" ? "#FCA500" : "transparent",
                            fontWeight: 600, textTransform: "uppercase", borderRadius: "20px", px: 2.5, py: 1,
                            "&:hover": { bgcolor: "#FCA500", color: "#fff" },
                          }}
                        >
                          {item}
                        </Button>
                        <Menu
                          anchorEl={anchorElDesktop}
                          open={Boolean(anchorElDesktop)}
                          onClose={handleOutletDesktopClose}
                          PaperProps={{ elevation: 3, sx: { borderRadius: 2, minWidth: 180, mt: 1 } }}
                          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                          transformOrigin={{ vertical: "top", horizontal: "left" }}
                        >
                          {outlets.map((out) => (
                            <MenuItem key={out.id} onClick={() => handleOutletClick(out)} sx={{ px: 2.5, py: 1.25 }}>
                              {out.name}
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>
                    ) : (
                      <Button
                        key={item}
                        onClick={() => handleNavClick(item)}
                        sx={{
                          color: selectedNav === item ? "#fff" : "#FCA500",
                          bgcolor: selectedNav === item ? "#FCA500" : "transparent",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          borderRadius: "20px",
                          px: 2.5, py: 1,
                          "&:hover": { bgcolor: "#FCA500", color: "#fff" },
                        }}
                      >
                        {item}
                      </Button>
                    )
                  )}
                </Box>
              )}
            </Toolbar>
          </AppBar>
        </Box>
      </Container>
    </Box>
  );
};

export default Header;
